// frontend/src/pages/StockIn.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  Grid,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Delete,
  DeleteSweep,
  LocalPrintshop,
  Inventory,
} from "@mui/icons-material";
import API from "../api";
import { printReceipt } from "../utils/printer";

const INITIAL_AUTH_DIALOG = { open: false, type: "", targetId: null };

export default function StockIn({ loggedUser }) {
  const [products, setProducts] = useState([]);
  const [intakeList, setIntakeList] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [inputQty, setInputQty] = useState(1);
  const [unitCost, setUnitCost] = useState("");

  // Supplier & Voucher Audit Details
  const [supplierName, setSupplierName] = useState("");
  const [deliveryNoteRef, setDeliveryNoteRef] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // 🔐 Supervisor Override State
  const [authDialog, setAuthDialog] = useState(INITIAL_AUTH_DIALOG);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");

  // 🏢 Active workspace resolution
  const getActiveTenantId = () => localStorage.getItem("tenant_id") || "default";

  // 🔑 Auth header builder helper
  const getRequestHeaders = useCallback(() => {
    const headers = { "X-Tenant-ID": getActiveTenantId() };
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  // 📦 Fetch Catalog
  const fetchProducts = useCallback(async () => {
    try {
      const res = await API.get("/products/", {
        headers: getRequestHeaders(),
      });
      const fetchedProducts = res.data || [];
      setProducts(fetchedProducts);
      setError("");

      setIntakeList((prev) => {
        if (prev.length === 0) return prev;
        const validIds = new Set(fetchedProducts.map((p) => p.id));
        return prev.filter((item) => validIds.has(item.product_id));
      });
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError("Failed to load product catalog for intake operations.");
    }
  }, [getRequestHeaders]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handle product selection to auto-populate unit cost
  const handleProductSelect = (id) => {
    setSelectedProductId(id);
    const target = products.find((p) => p.id === id);
    if (target) {
      setUnitCost(target.price ? target.price.toString() : "0");
    }
  };

  // 🖩 Computed Total Values
  const grandTotal = useMemo(() => {
    return intakeList.reduce((sum, item) => sum + item.quantity * item.unit_cost, 0);
  }, [intakeList]);

  // ➕ Add Item to Intake List
  const handleAddToIntake = () => {
    if (!selectedProductId) return;
    const targetProduct = products.find((p) => p.id === selectedProductId);
    if (!targetProduct) return;

    if (inputQty <= 0) {
      setError("Intake quantity must be at least 1 unit.");
      return;
    }

    const parsedCost = parseFloat(unitCost) >= 0 ? parseFloat(unitCost) : targetProduct.price || 0;

    setIntakeList((prev) => {
      const existingIdx = prev.findIndex((item) => item.product_id === targetProduct.id);
      const updated = [...prev];

      if (existingIdx >= 0) {
        updated[existingIdx].quantity += inputQty;
        updated[existingIdx].unit_cost = parsedCost;
      } else {
        updated.push({
          product_id: targetProduct.id,
          productname: targetProduct.title || targetProduct.productname,
          current_stock: targetProduct.stock ?? targetProduct.quantity ?? 0,
          quantity: inputQty,
          unit_cost: parsedCost,
        });
      }
      setError("");
      return updated;
    });

    setSelectedProductId("");
    setInputQty(1);
    setUnitCost("");
  };

  // 🛡️ Open Override Dialog
  const requestVoidAuthorization = (type, targetId = null) => {
    setAuthError("");
    setAuthEmail("");
    setAuthPassword("");
    setAuthDialog({ open: true, type, targetId });
  };

  // 🔐 Verify Credentials for Voiding
  const handleVerifyOverride = async () => {
    setAuthError("");
    try {
      const res = await API.post(
        "/api/login",
        { username: authEmail, password: authPassword },
        { headers: getRequestHeaders() }
      );

      const userRole = (res.data?.user?.role || res.data?.role || "").toLowerCase();
      const isAuthorized = ["a", "s", "admin", "supervisor", "manager"].includes(userRole);

      if (isAuthorized) {
        if (authDialog.type === "item") {
          setIntakeList((prev) => prev.filter((item) => item.product_id !== authDialog.targetId));
        } else if (authDialog.type === "all") {
          setIntakeList([]);
        }
        setAuthDialog(INITIAL_AUTH_DIALOG);
        setError("");
      } else {
        setAuthError("🚫 Security Denied: Account lacks Supervisor clearance.");
      }
    } catch (err) {
      console.error("Override Failure:", err);
      setAuthError(err.response?.data?.detail || "❌ Authorization failed.");
    }
  };

  // 📥 Commit Intake to Database & Print Receipt/Docket
  const handleFinalizeStockIn = async () => {
    if (intakeList.length === 0) {
      setError("Intake rejected: Stock list is empty.");
      return;
    }

    if (!supplierName.trim()) {
      setError("Audit error: Supplier Name is mandatory for stock updates.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    const payload = {
      supplier_name: supplierName.trim(),
      delivery_note_ref: deliveryNoteRef.trim() || null,
      items: intakeList.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
      })),
    };

    try {
      const res = await API.post("/api/stock-in/", payload, {
        headers: getRequestHeaders(),
      });

      setSuccessMsg(`✅ Stock Quantity Updated! Intake Voucher: ${res.data.reference_no}`);

      // Forward to local printer utility for stock receiving docket printout
      try {
        await printReceipt({ stockIn: res.data });
      } catch (printErr) {
        setError("Stock updated in database, but receipt printer was unreachable.");
      }

      // Reset Form State
      setIntakeList([]);
      setSupplierName("");
      setDeliveryNoteRef("");
      fetchProducts();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to commit stock intake.");
    } finally {
      setLoading(false);
    }
  };

  const isCommitDisabled = loading || intakeList.length === 0 || !supplierName.trim();

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          📦 Stock Intake & Supplier Receiving
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active Workspace: <strong>{getActiveTenantId().toUpperCase()}</strong>
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      <Grid container spacing={3}>
        {/* Left Column: Product Selector and Receiving List */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 2, mb: 3, backgroundColor: "action.hover" }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📥 Select Product to Receive
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <Select
                  value={selectedProductId}
                  onChange={(e) => handleProductSelect(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "background.paper" }}
                >
                  <MenuItem value="" disabled>-- Select Stock Item --</MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title || p.productname} — [Current Stock: {p.stock ?? p.quantity ?? 0}]
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={6} sm={2}>
                <TextField
                  label="Cost ($)"
                  type="number"
                  size="small"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={6} sm={2}>
                <TextField
                  label="Add Qty"
                  type="number"
                  size="small"
                  value={inputQty}
                  onChange={(e) => setInputQty(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Inventory />}
                  onClick={handleAddToIntake}
                  disabled={!selectedProductId}
                  fullWidth
                >
                  Add Stock
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Pending Intake List Table */}
          <Paper elevation={3}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h5" fontWeight="bold">📋 Receiving Manifest</Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                disabled={intakeList.length === 0}
                onClick={() => requestVoidAuthorization("all")}
              >
                Clear Manifest
              </Button>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "primary.main" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Product Details</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Current Stock</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Added Qty</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Unit Cost</TableCell>

                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Subtotal</TableCell>
                  <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {intakeList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>
                      Manifest is empty. Select products above to append new stock orders.
                    </TableCell>
                  </TableRow>
                ) : (
                  intakeList.map((item) => (
                    <TableRow key={item.product_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{item.productname}</TableCell>
                      <TableCell>{item.current_stock}</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                        +{item.quantity}
                      </TableCell>
                      <TableCell>${item.unit_cost.toFixed(2)}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        ${(item.quantity * item.unit_cost).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => requestVoidAuthorization("item", item.product_id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Right Audit Verification & Confirmation Deck */}
        <Grid item xs={12} md={4}>
          <Paper elevation={4} sx={{ p: 3, backgroundColor: "#1e293b", color: "#fff", borderRadius: "8px" }}>
            <Typography variant="h5" fontWeight="bold" borderBottom="1px solid #334155" pb={1} mb={3}>
              🏢 Supplier & Voucher Audit
            </Typography>

            <TextField
              label="Supplier / Vendor Name *"
              variant="outlined"
              fullWidth
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              InputLabelProps={{ style: { color: "#38bdf8" } }}
              sx={{ ...styles.darkInput, mb: 2 }}
            />

            <TextField
              label="Delivery Note / Invoice Ref #"
              variant="outlined"
              fullWidth
              value={deliveryNoteRef}
              onChange={(e) => setDeliveryNoteRef(e.target.value)}
              InputLabelProps={{ style: { color: "#94a3b8" } }}
              sx={{ ...styles.darkInput, mb: 3 }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body1" color="#94a3b8">Items Received:</Typography>
              <Typography variant="body1" fontWeight="bold">{intakeList.length}</Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", mb: 3, backgroundColor: "#0f172a", p: 1.5, borderRadius: "6px", borderLeft: "4px solid #38bdf8" }}>
              <Typography variant="subtitle2" color="#38bdf8" sx={{ letterSpacing: "1px", fontWeight: "bold", textTransform: "uppercase" }}>
                Total Intake Cost Value
              </Typography>
              <Typography variant="h3" color="#38bdf8" fontWeight="900" sx={{ fontFamily: "monospace", textAlign: "right", mt: 0.5 }}>
                ${grandTotal.toFixed(2)}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LocalPrintshop />}
              onClick={handleFinalizeStockIn}
              disabled={isCommitDisabled}
              sx={{ py: 1.5, fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {loading ? "Committing Stock..." : "Commit Stock & Print Docket"}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* 🔐 SUPERVISOR OVERRIDE DIALOG */}
      <Dialog open={authDialog.open} onClose={() => setAuthDialog(INITIAL_AUTH_DIALOG)}>
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>🛡️ Supervisor Authorization Required</DialogTitle>
        <DialogContent sx={{ minWidth: "300px", display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {authError && <Alert severity="error">{authError}</Alert>}
          <Typography variant="body2" color="textSecondary">
            Supervisor authentication is required to clear inventory manifest items.
          </Typography>
          <TextField
            label="Supervisor Username / Email"
            variant="outlined"
            fullWidth
            value={authEmail}
            onChange={(e) => setAuthEmail(e.target.value)}
          />
          <TextField
            label="Password / PIN"
            type="password"
            variant="outlined"
            fullWidth
            value={authPassword}
            onChange={(e) => setAuthPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAuthDialog(INITIAL_AUTH_DIALOG)} color="inherit">Cancel</Button>
          <Button onClick={handleVerifyOverride} variant="contained" color="error">
            Verify & Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

const styles = {
  darkInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#0f172a !important",
      color: "#ffffff",
      borderRadius: "4px",
      "& fieldset": { borderColor: "#334155 !important" },
      "&:hover fieldset": { borderColor: "#38bdf8 !important" },
      "&.Mui-focused fieldset": { borderColor: "#38bdf8 !important" },
    },
  },
};